import React, { useState } from 'react';
import { apiFetch } from '../lib/api';

const PRICE_COLORS = {
  $: 'bg-green-100 text-green-800',
  $$: 'bg-yellow-100 text-yellow-800',
  $$$: 'bg-orange-100 text-orange-800',
  $$$$: 'bg-red-100 text-red-800',
};

export default function ItineraryView({ itinerary, onSave, onEmail, onBack, saveDisabled, saveLabel, saved, user, savedItineraryId, readOnly }) {
  const [shareUrl, setShareUrl] = useState('');
  const [publicId, setPublicId] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [copied, setCopied] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  // Lazily mint the public share link the first time any share button is pressed.
  const ensureShareLink = async () => {
    if (shareUrl) return { share_url: shareUrl, public_id: publicId };
    if (!savedItineraryId) {
      alert('Save the trip to your profile first, then share.');
      return null;
    }
    setCreatingLink(true);
    try {
      const res = await apiFetch(`/api/itineraries/${savedItineraryId}/share`, { method: 'POST' });
      setShareUrl(res.share_url);
      setPublicId(res.public_id);
      return res;
    } catch (e) {
      alert(e.message || 'Could not create share link.');
      return null;
    } finally {
      setCreatingLink(false);
    }
  };

  const tweetText = (loc) => `Just planned a ${itinerary?.summary?.duration || itinerary?.duration + ' day'} Alberta food trip around ${loc || 'Alberta'} on @culinera 🍽️`;

  const oneTapTwitter = async () => {
    const res = await ensureShareLink();
    if (!res) return;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText(itinerary?.summary?.location))}&url=${encodeURIComponent(res.share_url)}`;
    window.open(url, '_blank', 'noopener');
  };

  const oneTapInstagram = async () => {
    const res = await ensureShareLink();
    if (!res) return;
    // Trigger PNG download in a new tab — works on iOS Safari, Android Chrome, and desktop
    window.open(`${BACKEND}/api/og/trip/${res.public_id}/story.png`, '_blank', 'noopener');
  };

  // Native mobile share sheet — gives a true single-tap to Messages, IG, X, anything installed.
  const oneTapNativeShare = async () => {
    const res = await ensureShareLink();
    if (!res) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: itinerary.title,
          text: tweetText(itinerary?.summary?.location),
          url: res.share_url,
        });
      } catch {
        // user cancelled — silent
      }
    } else {
      // desktop fallback: copy to clipboard
      await navigator.clipboard.writeText(res.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyShareUrl = async () => {
    const res = await ensureShareLink();
    if (!res) return;
    try {
      await navigator.clipboard.writeText(res.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = res.share_url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!itinerary) return null;
  const { title, days = [], restaurants = [], foodTrucks = [], summary = {} } = itinerary;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </div>
          <button data-testid="itinerary-back-btn" onClick={onBack} className="text-teal-600 hover:text-teal-700 font-semibold">← Back</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{title}</h1>
          <div className="text-sm opacity-95 flex flex-wrap gap-x-4 gap-y-1">
            <span>📍 {summary.location}</span>
            <span>🗓 {summary.duration}</span>
            {summary.groupSize && <span>👥 {summary.groupSize}</span>}
            {summary.budget && <span>💰 {summary.budget}</span>}
          </div>
          {summary.cuisines?.length ? (
            <div className="mt-2 text-sm opacity-90">Cuisines: {summary.cuisines.join(', ')}</div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3 mb-6" data-testid="itinerary-actions">
          {onSave && !readOnly && (
            <button
              data-testid="save-itinerary-btn"
              onClick={onSave}
              disabled={saveDisabled || saved}
              className="bg-teal-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-teal-700 disabled:opacity-60"
            >{saved ? '✓ Saved to your profile' : saveLabel || '💾 Save to my profile'}</button>
          )}
          {onEmail && !readOnly && (
            <button
              data-testid="email-itinerary-btn"
              onClick={onEmail}
              className="border border-teal-600 text-teal-700 px-5 py-2 rounded-lg font-semibold hover:bg-teal-50"
            >✉ Email me a copy</button>
          )}
          {savedItineraryId && !readOnly && (
            <>
              {/* Mobile-first: native share sheet is the primary CTA. Desktop hides this in favour of explicit X/IG buttons. */}
              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  data-testid="native-share-btn"
                  onClick={oneTapNativeShare}
                  disabled={creatingLink}
                  className="sm:hidden flex-1 min-w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-5 py-3 rounded-lg font-bold text-base hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 shadow-md"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.25a.75.75 0 0 1 .53.22l4.25 4.25a.75.75 0 0 1-1.06 1.06L12.75 4.81V14a.75.75 0 0 1-1.5 0V4.81L8.28 7.78a.75.75 0 0 1-1.06-1.06l4.25-4.25a.75.75 0 0 1 .53-.22Zm-7 9.5a.75.75 0 0 1 .75.75v6.75a1.5 1.5 0 0 0 1.5 1.5h9.5a1.5 1.5 0 0 0 1.5-1.5V12.5a.75.75 0 0 1 1.5 0v6.75a3 3 0 0 1-3 3h-9.5a3 3 0 0 1-3-3V12.5a.75.75 0 0 1 .75-.75Z"/></svg>
                  Share trip
                </button>
              )}

              <button
                data-testid="share-twitter-btn"
                onClick={oneTapTwitter}
                disabled={creatingLink}
                className="bg-black text-white px-5 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                Share to X
              </button>
              <button
                data-testid="share-instagram-btn"
                onClick={oneTapInstagram}
                disabled={creatingLink}
                className="bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white px-5 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4c0 3.2-2.6 5.8-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8C2 4.6 4.6 2 7.8 2zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.4 5.6 20 7.6 20h8.8c2 0 3.6-1.6 3.6-3.6V7.6C20 5.6 18.4 4 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
                Share to Instagram
              </button>
              <button
                data-testid="copy-share-link-btn"
                onClick={copyShareUrl}
                disabled={creatingLink}
                className="border border-purple-600 text-purple-700 px-5 py-2 rounded-lg font-semibold hover:bg-purple-50 disabled:opacity-60"
              >{copied ? '✓ Link copied!' : creatingLink ? 'Creating link…' : '🔗 Copy share link'}</button>
            </>
          )}
          {!user && onSave && !readOnly && (
            <span className="self-center text-sm text-gray-500">Sign in to save and revisit this trip anywhere.</span>
          )}
        </div>

        {shareUrl && !readOnly && (
          <div data-testid="share-url-panel" className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="text-sm font-semibold text-purple-800 mb-1">Your public trip link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} className="flex-1 bg-white border border-purple-200 rounded px-3 py-2 text-sm text-gray-800" onFocus={(e) => e.target.select()} />
              <button onClick={copyShareUrl} className="bg-purple-600 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-purple-700">{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <p className="text-xs text-purple-700 mt-2">Anyone with this link can view your trip — no sign-in needed. The link preview on Facebook, X, iMessage and WhatsApp uses an auto-generated card with your trip title and location.</p>
          </div>
        )}

        <section className="space-y-6">
          {days.map((d) => (
            <div key={d.day} data-testid={`itinerary-day-${d.day}`} className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Day {d.day}</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {d.meals.map((m, i) => {
                  const r = m.restaurant;
                  return (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-white to-teal-50/30">
                      <div className="text-xs font-semibold text-teal-700 uppercase tracking-wide mb-1">{m.slot}</div>
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-semibold text-gray-900">{r.name}</div>
                        <span className={`text-xs ${PRICE_COLORS[r.price]} px-2 py-0.5 rounded`}>{r.price}</span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">📍 {r.city}{r.neighborhood ? ` · ${r.neighborhood}` : ''}</div>
                      <p className="text-sm text-gray-700 line-clamp-3">{r.description}</p>
                      <div className="text-xs text-gray-600 mt-2">{r.cuisine}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {foodTrucks.length > 0 && (
          <section className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Street food bonus 🚚</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {foodTrucks.map((t) => (
                <div key={t.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="font-semibold text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-500 mb-1">📍 {t.city}</div>
                  <p className="text-sm text-gray-700">{t.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Full restaurant list</h2>
          <p className="text-sm text-gray-500 mb-4">Every spot on your trip, so you can share it or save links for reservations.</p>
          <ul className="grid md:grid-cols-2 gap-2">
            {restaurants.map((r) => (
              <li key={r.id} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-teal-600">●</span>
                <span><strong>{r.name}</strong> — {r.city} · {r.cuisine} · {r.price}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
